/* @flow */

// Note: Translations not needed for paid site.

import * as React from 'react';
import { connect } from 'react-redux';
import numeral from 'numeral';
import get from 'lodash/get';
import { isEmpty } from 'lodash/fp';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';

import {
  getWithdrawalDetails,
  resetWithdrawDetails,
  withdrawWinnings
} from 'app/actions/userActions';
import Container from 'app/components/layout/Container';
import Tooltip from 'app/components/commons/Tooltip';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Title from 'app/components/commons/Title';
import WithdrawForm from 'app/components/user/WithdrawForm';
import { APPROVED, PENDING, REJECTED } from 'app/constants/withdrawStatusTypes';
import DocumentUploader from 'app/components/user/DocumentUploader';
import Button from 'app/components/commons/Button';
import Loader from 'app/components/commons/Loader';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import t from 'app/utils/translate';
import VerifyIdLink from 'app/components/links/VerifyIdLink';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';

// 2 possible shapes of this type
type WithdrawInfo =
  | {
      account_name: string,
      account_number: string,
      amount: number,
      bank_name?: string,
      bsb?: string,
      document: string, // dataUri
      swift_code?: string
    }
  | {
      amount: number,
      payment_id: string
    };

type Field = {
  key: string,
  label: string,
  placeholder: string,
  type: string
};

type ErrorMessage = {
  key: string,
  message: string
};

type Props = {
  currencyCode: string,
  currencySymbol: string,
  currentUser: Object,
  darkMode: boolean,
  documentErrors: ?Array<Object>,
  errors?: Array<ErrorMessage>,
  hasLightBackground: boolean,
  fields: Array<Field>,
  isFreeOnly: boolean,
  isUploadingDocument: boolean,
  onFetch: () => void,
  onReset: () => void,
  onWithdraw: Object => void,
  retrievingWithdrawalAccounts: boolean,
  siteName: string,
  withdrawalDetails: ?{
    id: number,
    label: string,
    status: {
      id: number,
      name: string
    }
  },
  withdrawalAmount?: string,
  withdrawingWinnings: boolean
};

type State = {
  isDocumentUploaderOpen: boolean
};

const VerifyLink = styled(VerifyIdLink)(({ theme }) => ({
  color: theme.color.primary,
  textTransform: 'uppercase',
  textDecoration: 'underline'
}));

const Message = styled.span(({ darkMode, theme }) => ({
  color: darkMode ? theme.color.basis5 : theme.color.invertableBasis5,
  opacity: 0.8,
  fontSize: '0.9em'
}));

const InfoButton = styled.span({
  cursor: 'pointer'
});

const ToolTipButton = styled.div({
  display: 'inline-block',
  verticalAlign: 'middle',
  width: '30px'
});

export class AccountWithdraw extends React.Component<Props, State> {
  props: Props;

  state: State = {
    isDocumentUploaderOpen: false
  };

  static defaultProps = {
    errors: [],
    withdrawalAmount: undefined
  };

  componentDidMount() {
    const { currentUser, onFetch } = this.props;
    if (currentUser.id_verified) {
      onFetch();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isUploadingDocument } = this.props;

    if (
      isUploadingDocument &&
      !nextProps.isUploadingDocument &&
      isEmpty(nextProps.documentErrors)
    ) {
      this.setState({
        isDocumentUploaderOpen: false
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { currentUser, onFetch } = this.props;

    if (!prevProps.currentUser.id_verified && currentUser.id_verified) {
      onFetch();
    }
  }

  componentWillUnmount() {
    const { onReset } = this.props;
    onReset();
  }

  handleWithdrawClicked = (withdrawalInfo: WithdrawInfo) => {
    const { onWithdraw } = this.props;

    onWithdraw(withdrawalInfo);
  };

  openDocumentUploader = () => {
    this.setState({
      isDocumentUploaderOpen: true
    });
  };

  render() {
    const {
      currencyCode,
      currencySymbol,
      currentUser,
      darkMode,
      documentErrors,
      errors,
      hasLightBackground,
      fields,
      isFreeOnly,
      isUploadingDocument,
      retrievingWithdrawalAccounts,
      siteName,
      withdrawalAmount,
      withdrawalDetails,
      withdrawingWinnings
    } = this.props;
    const { isDocumentUploaderOpen } = this.state;

    if (retrievingWithdrawalAccounts) {
      return <Loader kind={hasLightBackground ? 'dark' : 'light'} />;
    }

    const withdrawalStatus = get(withdrawalDetails, 'status.id');

    return (
      <div className="page-container">
        <Helmet title="Withdraw" />
        <NotificationHandler
          afterCond={!withdrawingWinnings && isEmpty(errors)}
          beforeCond={withdrawingWinnings}
          type="success"
        >
          <span>
            You have successfully withdrawn <CurrencyIcon />
            {withdrawalAmount}
          </span>
        </NotificationHandler>
        <NotificationHandler
          afterCond={!isUploadingDocument && isEmpty(documentErrors)}
          beforeCond={isUploadingDocument}
          type="success"
        >
          {t('Upload successful')}
        </NotificationHandler>
        <Title>Withdraw funds</Title>
        <h3 className="text-center">
          <Message darkMode={darkMode}>Account Balance: </Message>
          <CurrencyIcon />
          {numeral(currentUser.balance).format('0,0.00')}
        </h3>
        <h3 className="text-center">
          <ToolTipButton>
            <Tooltip
              title={`${siteName} requires that any deposits into a Member's Account be turned over at least once or 'bet through' prior to requesting a withdrawal from their Account.`}
              trigger="click"
            >
              <InfoButton className="ion-information-circled" />
            </Tooltip>
          </ToolTipButton>
          <Message darkMode={darkMode}>Withdrawable Balance: </Message>
          <CurrencyIcon />
          {numeral(currentUser.availableBalance).format('0,0.00')}
        </h3>
        <hr />
        {withdrawalStatus && withdrawalStatus !== APPROVED && (
          <Container size="small">
            {withdrawalStatus === REJECTED && (
              <FormValidationMessage
                kind="danger"
                message="There was a problem verifying your bank account. Please upload new document/s or contact support."
              />
            )}
            {withdrawalStatus === PENDING && (
              <FormValidationMessage
                kind="warning"
                message="Your account verification is pending for approval."
              />
            )}
            {isDocumentUploaderOpen ? (
              <DocumentUploader />
            ) : (
              <Button block size="medium" kind="info" onClick={this.openDocumentUploader}>
                Upload new document
              </Button>
            )}
          </Container>
        )}
        <hr />
        {!currentUser.id_verified ? (
          <div>
            <div className="form-message text-center">
              <h3>Your identity has not been verified yet</h3>
              <VerifyLink>
                {isFreeOnly ? 'Verify now to claim your prize' : 'Verify now to withdraw winnings'}
              </VerifyLink>
            </div>
          </div>
        ) : (
          <Container size="small">
            {errors.length > 0 && <FormValidationMessage message={errors} />}
            <WithdrawForm
              availableBalance={currentUser.availableBalance}
              currencyCode={currencyCode}
              currencySymbol={currencySymbol}
              lightContent={hasLightBackground}
              fields={fields}
              fullName={`${currentUser.first_name} ${currentUser.last_name}`}
              isSubmitting={withdrawingWinnings}
              onClick={this.handleWithdrawClicked}
              withdrawalDetails={withdrawalDetails}
            />
          </Container>
        )}
      </div>
    );
  }
}

function mapStateToProps({ settings, user }) {
  const countryCode = get(user, 'currentUser.country.iso_alpha_2_code', '').toLowerCase();

  return {
    currencyCode: get(settings, 'currency.code', 'AUD'),
    currencySymbol: get(settings, 'currency.symbol', '$'),
    currentUser: user.currentUser,
    darkMode: settings.darkMode,
    documentErrors: user.uploadingDocumentErrors,
    errors: user.withdrawalErrors,
    hasLightBackground: !settings.darkMode,
    fields: get(settings.withdrawForm[countryCode], 'fields', []),
    isFreeOnly: settings.freeOnly,
    isSignedIn: user.isSignedIn,
    isUploadingDocument: user.uploadingDocument,
    retrievingWithdrawalAccounts: user.retrievingWithdrawalAccounts,
    siteName: settings.siteName,
    withdrawalAmount: user.withdrawalAmount,
    withdrawalDetails: user.withdrawalDetails,
    withdrawingWinnings: user.withdrawingWinnings
  };
}

const actions = {
  onFetch: getWithdrawalDetails,
  onReset: resetWithdrawDetails,
  onWithdraw: withdrawWinnings
};

const connector = connect(
  mapStateToProps,
  actions
);

export default connector(AccountWithdraw);
